import { create } from 'zustand'
import type { UserStatus } from '@paperair/shared-types'
import {
  UserWithAppCount,
  listUsers,
  getErrorMessage,
} from '../api/usersApi'

interface UsersState {
  users: UserWithAppCount[]
  total: number
  page: number
  pages: number
  pageSize: number
  isLoading: boolean
  error: string | null

  // Filter state
  statusFilter: UserStatus | null
  searchQuery: string

  // Sort state
  sortBy: string
  sortDir: 'asc' | 'desc'

  // Actions
  fetchUsers: () => Promise<void>
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void
  setStatusFilter: (status: UserStatus | null) => void
  setSearchQuery: (query: string) => void
  setSort: (sortBy: string, sortDir: 'asc' | 'desc') => void
  clearError: () => void
}

export const useUsersStore = create<UsersState>()((set, get) => ({
  users: [],
  total: 0,
  page: 1,
  pages: 0,
  pageSize: 20,
  isLoading: false,
  error: null,
  statusFilter: null,
  searchQuery: '',
  sortBy: 'email',
  sortDir: 'asc',

  fetchUsers: async () => {
    const { page, pageSize, statusFilter, searchQuery, sortBy, sortDir } = get()
    set({ isLoading: true, error: null })

    try {
      const response = await listUsers({
        page,
        limit: pageSize,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        sortBy,
        sortDir,
      })
      set({
        users: response.users,
        total: response.total,
        page: response.page,
        pages: response.pages,
        isLoading: false,
      })
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
    }
  },

  setPage: (page: number) => {
    set({ page })
    get().fetchUsers()
  },

  setPageSize: (pageSize: number) => {
    set({ pageSize, page: 1 })
    get().fetchUsers()
  },

  setStatusFilter: (status: UserStatus | null) => {
    set({ statusFilter: status, page: 1 })
    get().fetchUsers()
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query, page: 1 })
    get().fetchUsers()
  },

  setSort: (sortBy: string, sortDir: 'asc' | 'desc') => {
    set({ sortBy, sortDir, page: 1 })
    get().fetchUsers()
  },

  clearError: () => set({ error: null }),
}))
